import * as React from 'react';
import { Link, Navigate, useNavigate } from '@tanstack/react-router';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  WorkspaceButton,
  WorkspaceHeader,
  WorkspaceNavTab,
  WorkspaceNavTabs,
} from '@/components/ui/workspace';
import { ApiError } from '@/lib/api/client';
import { useDiscoverySummaryQuery, type DiscoverySummary } from '@/lib/api/discovery';
import { ClientFactsDialog } from './client-facts/client-facts';
import { VersionMoveDialog } from './version-move-dialog';
import {
  leadText,
  newerVersion,
  plural,
  sourcesRuleText,
  tabCounts,
  type DiscoveryDestination,
  type DiscoverySection,
} from './discovery-logic';

/**
 * Engagement -> Discovery: the module's frame (T-0185, E12-F02).
 *
 * Owns what every Discovery screen shares: the summary from the API, the
 * module-off state, the one-direction routing (setup until discovery has
 * started, never after), and the header with the four tabs. Prototype:
 * `engPage` in `docs/discovery/prototype/discovery.js`.
 *
 *   - Module off (or no access): the API answers not_found; the screen
 *     says so plainly, as Selection does.
 *   - Not started: every Discovery address lands on Set up.
 *   - Started: Set up is gone; the header and tabs frame the screen.
 *
 * The frame is the same full-height card Selection uses, so the two
 * modules read as one product.
 */

const FRAME =
  'flex h-[calc(100vh-56px-48px)] min-h-[480px] flex-col overflow-hidden rounded-card border border-ink-100 bg-paper-0';

export function DiscoveryLayout({
  engagementId,
  section,
  children,
}: {
  engagementId: string;
  /** The Discovery screen the address names, or null once it has left the module. */
  section: DiscoverySection | null;
  children: React.ReactNode;
}) {
  const q = useDiscoverySummaryQuery(engagementId);

  // The address has left Discovery (a sidebar link, Cancel on Set up): there
  // is nothing to route, and the screen that was showing stays until the new
  // one takes over. Deciding on the destination's path sent people back to
  // Set up before discovery had started (13 October 2026).
  if (section === null) return <div className={FRAME}>{children}</div>;

  if (q.error) {
    if (q.error instanceof ApiError && q.error.code === 'not_found') {
      return (
        <div className={FRAME}>
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
            <h1 className="text-h2 font-semibold text-ink-900">Discovery is not available here</h1>
            <p className="max-w-md text-body-sm text-ink-500">
              The Discovery module is not turned on for this engagement, or you do not have access
              to it. Turning Discovery on also turns Selection on, where its requirements go.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className={FRAME}>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8">
          <ErrorMessage error={q.error} />
          <WorkspaceButton onClick={() => void q.refetch()}>Try again</WorkspaceButton>
        </div>
      </div>
    );
  }
  if (!q.data) {
    return (
      <div className={FRAME}>
        <div className="flex flex-1 items-center justify-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading discovery…
        </div>
      </div>
    );
  }

  const s = q.data;
  // One direction: Set up until discovery starts, never after.
  if (!s.started && section !== 'setup') {
    return (
      <Navigate to="/engagements/$engagementId/discovery/setup" params={{ engagementId }} replace />
    );
  }
  if (s.started && section === 'setup') {
    return <Navigate to="/engagements/$engagementId/discovery" params={{ engagementId }} replace />;
  }
  if (section === 'setup') return <div className={FRAME}>{children}</div>;

  return (
    <div className={FRAME}>
      <WorkspaceHeader title="Discovery" meta={<HeaderMeta engagementId={engagementId} s={s} />} />
      <DiscoveryTabs engagementId={engagementId} section={section} s={s} />
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </div>
  );
}

const LINKISH =
  'rounded-chip font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600';

function HeaderMeta({ engagementId, s }: { engagementId: string; s: DiscoverySummary }) {
  const [factsOpen, setFactsOpen] = React.useState(false);
  const [moveOpen, setMoveOpen] = React.useState(false);
  const newer = newerVersion(s);
  const lead = leadText(s.lead);
  const parts: React.ReactNode[] = [
    `${s.framework?.name ?? ''} framework v${s.framework?.version ?? ''}`,
    plural(s.kpis.areas, 'area'),
    sourcesRuleText(s),
    ...(lead ? [lead] : []),
    <button key="facts" type="button" className={LINKISH} onClick={() => setFactsOpen(true)}>
      Client facts
    </button>,
  ];
  if (newer !== null) {
    parts.push(
      s.permissions.can_move_version ? (
        <button key="move" type="button" className={LINKISH} onClick={() => setMoveOpen(true)}>
          v{newer} is available
        </button>
      ) : (
        <span key="move" title="Only the engagement lead can move this engagement to it.">
          v{newer} is available
        </span>
      ),
    );
  }
  return (
    <>
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 ? ' · ' : null}
          {p}
        </React.Fragment>
      ))}
      <ClientFactsDialog engagementId={engagementId} open={factsOpen} onOpenChange={setFactsOpen} />
      {moveOpen && newer !== null && s.framework ? (
        <VersionMoveDialog
          engagementId={engagementId}
          frameworkName={s.framework.name}
          from={s.framework.version}
          to={newer}
          open
          onOpenChange={setMoveOpen}
        />
      ) : null}
    </>
  );
}

function DiscoveryTabs({
  engagementId,
  section,
  s,
}: {
  engagementId: string;
  section: DiscoverySection;
  s: DiscoverySummary;
}) {
  const counts = tabCounts(s);
  const params = { engagementId };
  const waiting =
    s.kpis.awaiting_review > 0 ? (
      <span
        title="Evidence waiting for review"
        className="h-1.5 w-1.5 rounded-full bg-accent-amber-600"
      >
        <span className="sr-only">Evidence waiting for review</span>
      </span>
    ) : null;
  return (
    <WorkspaceNavTabs aria-label="Discovery">
      <WorkspaceNavTab asChild active={section === 'overview'}>
        <Link to="/engagements/$engagementId/discovery" params={params}>
          Overview
        </Link>
      </WorkspaceNavTab>
      <WorkspaceNavTab
        asChild
        active={section === 'sessions'}
        count={counts.sessions}
        indicator={waiting}
      >
        <Link to="/engagements/$engagementId/discovery/sessions" params={params}>
          Sessions
        </Link>
      </WorkspaceNavTab>
      <WorkspaceNavTab asChild active={section === 'findings'} count={counts.findings}>
        <Link to="/engagements/$engagementId/discovery/findings" params={params}>
          Findings
        </Link>
      </WorkspaceNavTab>
      <WorkspaceNavTab asChild active={section === 'maps'} count={counts.maps}>
        <Link to="/engagements/$engagementId/discovery/maps" params={params}>
          Process maps
        </Link>
      </WorkspaceNavTab>
    </WorkspaceNavTabs>
  );
}

/** Go to a Discovery place (or Selection) named by the screen logic. */
export function useDiscoveryNavigate(engagementId: string) {
  const navigate = useNavigate();
  return React.useCallback(
    (d: DiscoveryDestination) => {
      const params = { engagementId };
      switch (d.to) {
        case 'overview':
          void navigate({ to: '/engagements/$engagementId/discovery', params });
          break;
        case 'sessions':
          void navigate({
            to: '/engagements/$engagementId/discovery/sessions',
            params,
            search: {
              interview: d.interview,
              person: d.person,
              review: d.review,
              intake: d.intake,
            },
          });
          break;
        case 'findings':
          void navigate({
            to: '/engagements/$engagementId/discovery/findings',
            params,
            search: { area: d.area, status: d.status },
          });
          break;
        case 'maps':
          void navigate({
            to: '/engagements/$engagementId/discovery/maps',
            params,
            search: d.process ? { process: d.process } : {},
          });
          break;
        case 'signoff':
          void navigate({ to: '/engagements/$engagementId/discovery/signoff', params });
          break;
        case 'selection':
          void navigate({
            to: '/engagements/$engagementId/selection',
            params,
            search: d.add ? { add: d.add } : {},
          });
          break;
        case 'frameworks':
          void navigate({ to: '/settings/discovery-frameworks' });
          break;
      }
    },
    [navigate, engagementId],
  );
}
